import { useMemo, useCallback } from 'react'
import type { TemplateSection } from 'shared/api/templateService'

interface UseWorkflowStageMetadataParams {
  workflowSections: TemplateSection[]
  workflowStageSequences: Record<string, number> | null | undefined
  activeApprovalStageId: string | null
  activeStageCode: string | null
  currentStageSequence: number | null
}

export interface WorkflowStageMetadata {
  normalizedActiveStageId: string | null
  normalizedActiveStageCode: string | null
  stageSequenceMap: Record<string, number> | null
  templateStageOrderMap: Record<string, number>
  activeStageTemplateIndex: number
  activeStageSequence: number | null
  doesSectionMatchActiveStage: (section: TemplateSection) => boolean
}

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const useWorkflowStageMetadata = ({
  workflowSections,
  workflowStageSequences,
  activeApprovalStageId,
  activeStageCode,
  currentStageSequence,
}: UseWorkflowStageMetadataParams): WorkflowStageMetadata => {
  const normalizedActiveStageId = useMemo(
    () => activeApprovalStageId?.trim().toLowerCase() ?? null,
    [activeApprovalStageId],
  )

  const normalizedActiveStageCode = useMemo(
    () => activeStageCode?.trim().toUpperCase() ?? null,
    [activeStageCode],
  )

  const stageSequenceMap = useMemo(() => {
    if (!workflowStageSequences) {
      return null
    }
    const map: Record<string, number> = {}

    Object.entries(workflowStageSequences).forEach(([key, value]) => {
      if (typeof value !== 'number') {
        return
      }
      const trimmed = key.trim()
      if (!trimmed) {
        return
      }
      const normalizedKey = GUID_REGEX.test(trimmed) ? trimmed.toLowerCase() : trimmed.toUpperCase()
      map[normalizedKey] = value
    })
    return map
  }, [workflowStageSequences])

  const templateStageOrderMap = useMemo(() => {
    const map: Record<string, number> = {}
    let autoIndex = 0

    const registerOrder = (key: string | null | undefined, order: number) => {
      const normalized = key?.trim()
      if (!normalized || Object.prototype.hasOwnProperty.call(map, normalized)) {
        return
      }
      map[normalized] = order
    }

    workflowSections.forEach((section) => {
      const order = typeof section.orderIndex === 'number' ? section.orderIndex : autoIndex++
      registerOrder(section.approvalStageId?.trim().toLowerCase(), order)
      registerOrder(section.code?.trim().toUpperCase(), order)
    })

    return map
  }, [workflowSections])

  const doesSectionMatchActiveStage = useCallback(
    (section: TemplateSection) => {
      const sectionStageId = section.approvalStageId?.trim().toLowerCase()
      const sectionStageCode = section.code?.trim().toUpperCase()
      if (sectionStageId && normalizedActiveStageId && sectionStageId === normalizedActiveStageId) {
        if (normalizedActiveStageCode) {
          const hasExactCodeInCurrentStage = workflowSections.some((candidate) => {
            const candidateStageId = candidate.approvalStageId?.trim().toLowerCase()
            const candidateCode = candidate.code?.trim().toUpperCase()
            return candidateStageId === normalizedActiveStageId && candidateCode === normalizedActiveStageCode
          })

          // Only enforce code-level lock when that code truly exists in this stage.
          if (hasExactCodeInCurrentStage) {
            return sectionStageCode === normalizedActiveStageCode
          }
        }
        return true
      }

      if (sectionStageCode && normalizedActiveStageCode && sectionStageCode === normalizedActiveStageCode) {
        return true
      }
      return false
    },
    [normalizedActiveStageCode, normalizedActiveStageId, workflowSections],
  )

  const activeStageTemplateIndex = useMemo(() => {
    const resolveFromMap = (key: string | null) => {
      if (!key) {
        return undefined
      }
      const value = templateStageOrderMap[key]
      return typeof value === 'number' ? value : undefined
    }

    const byId = resolveFromMap(normalizedActiveStageId)
    if (typeof byId === 'number') {
      return byId
    }

    const byCode = resolveFromMap(normalizedActiveStageCode)
    if (typeof byCode === 'number') {
      return byCode
    }

    if (normalizedActiveStageId) {
      const idx = workflowSections.findIndex(
        (section) => section.approvalStageId?.trim().toLowerCase() === normalizedActiveStageId,
      )
      if (idx >= 0) {
        return idx
      }
    }

    if (normalizedActiveStageCode) {
      const idx = workflowSections.findIndex(
        (section) => section.code?.trim().toUpperCase() === normalizedActiveStageCode,
      )
      if (idx >= 0) {
        return idx
      }
    }

    return -1
  }, [normalizedActiveStageId, normalizedActiveStageCode, templateStageOrderMap, workflowSections])

  const activeStageSequence = useMemo(() => {
    if (typeof currentStageSequence === 'number') {
      return currentStageSequence
    }

    const lookupSequence = (key: string | null) => {
      if (!key || !stageSequenceMap) {
        return undefined
      }
      const value = stageSequenceMap[key]
      return typeof value === 'number' ? value : undefined
    }

    const sequenceById = lookupSequence(normalizedActiveStageId)
    if (typeof sequenceById === 'number') {
      return sequenceById
    }

    const sequenceByCode = lookupSequence(normalizedActiveStageCode)
    if (typeof sequenceByCode === 'number') {
      return sequenceByCode
    }

    const templateIndexById = templateStageOrderMap[normalizedActiveStageId ?? '']
    if (typeof templateIndexById === 'number') {
      return templateIndexById
    }

    const templateIndexByCode = templateStageOrderMap[normalizedActiveStageCode ?? '']
    if (typeof templateIndexByCode === 'number') {
      return templateIndexByCode
    }

    return null
  }, [
    currentStageSequence,
    normalizedActiveStageId,
    normalizedActiveStageCode,
    stageSequenceMap,
    templateStageOrderMap,
  ])

  return {
    normalizedActiveStageId,
    normalizedActiveStageCode,
    stageSequenceMap,
    templateStageOrderMap,
    activeStageTemplateIndex,
    activeStageSequence,
    doesSectionMatchActiveStage,
  }
}
